import { useCallback } from 'react';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';

import { snackbar } from '@occc-app/design-system';
import {
  MarketingContentFragment,
  Offer_Presentation_Type,
  OfferFragment,
  User_Role,
} from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';
import {
  useMarketingContentCreateMutation,
  useMarketingContentUpdateMutation,
} from '@occc-app/dataAccessLayer/marketingContent/mutations';
import { useOfferUpdateMutation } from '@occc-app/dataAccessLayer/offer/mutations';
import {
  getCustomDataFromFormValues,
  getMarketingContentDirtyFieldsValues,
  isFieldDirty,
} from '@occc-app/offerHelpers/utils';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { useUserSelector } from '@occc-app/App/AppContext';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { useOfferDisplayCategoriesCreateOrUpdateMutation } from '@occc-app/offerHelpers/DesignForm/useOfferDisplayCategoriesCreateOrUpdateMutation';

import { DesignFormContent } from './DesignFormContent';
import { AwardPresentationTypeWatcher } from './presentation-type.watcher';
import { useIsOrderAheadEnabled } from './utils';

import { validateDesignForm, DesignFormValues } from '../../schemas';
import { CONFIGURE_PAGE_STATE, DesignFormProps, DesignFormRenderProps } from '../../types';

type MarketingContentUpdateParams = {
  dirtyFields: Record<string, any>;
  values: {
    marketingContent: Partial<MarketingContentFragment>;
    [key: string]: any;
  };
};

type DesignFormContainerProps = {
  award: OfferFragment;
  refetchAward: () => void;
  setPageState?: (value: CONFIGURE_PAGE_STATE) => void;
  handleCancel: (hasUnsavedChanges?: boolean) => void;
  currentFlowType?: Flow_Types;
};

// TODO: refactor and if it is possible - use useGetDesignFormProps
export const DesignFormContainer = ({
  award,
  refetchAward,
  setPageState,
  handleCancel,
  currentFlowType = Flow_Types.Update,
}: DesignFormContainerProps) => {
  const currentUser = useUserSelector();
  const isReadOnlyRole = currentUser?.userRole === User_Role.AdminReadOnly;
  const isOrderAheadEnabled = useIsOrderAheadEnabled(award);

  const onUpdateComplete = () => {
    snackbar.success('Award marketing content was updated.');
  };

  const [marketingContentCreateMutation, { loading: createLoading }] =
    useMarketingContentCreateMutation();

  const [marketingContentUpdateMutation, { loading: updateLoading }] =
    useMarketingContentUpdateMutation({
      onCompleted: onUpdateComplete,
    });

  const [offerUpdate, { loading: offerUpdateLoading }] = useOfferUpdateMutation({
    onCompleted: onUpdateComplete,
  });

  const [displayCategoriesCreateOrUpdate, displayCategoriesLoading] =
    useOfferDisplayCategoriesCreateOrUpdateMutation();

  const loading = createLoading || updateLoading || offerUpdateLoading || displayCategoriesLoading;

  const marketingContent = award.marketingContents[0];

  const marketingContentCreate = async (values: DesignFormValues) => {
    await marketingContentCreateMutation({
      variables: {
        input: {
          ...values.marketingContent,
          offerId: award.id,
          locale: 'en_US',
          customData: getCustomDataFromFormValues(values.customData),
        },
      },
    });
  };

  const marketingContentUpdate = async ({ values, dirtyFields }: MarketingContentUpdateParams) => {
    const marketingContentValues = getMarketingContentDirtyFieldsValues({ values, dirtyFields });
    await marketingContentUpdateMutation({
      variables: {
        input: {
          id: marketingContent.id,
          locale: 'en_US',
          ...marketingContentValues,
          customData: getCustomDataFromFormValues(values.customData),
        },
      },
    });
  };

  const onSubmit = async (values: DesignFormValues, form: FormApi<DesignFormValues>) => {
    const { dirtyFields } = form.getState();
    const isOfferContentDirty = isFieldDirty<DesignFormValues>(dirtyFields, 'offer');
    const isDisplayCategoriesDirty = isFieldDirty<DesignFormValues>(
      dirtyFields,
      'displayCategories',
    );

    // Display categories are only valid for the Tile (Default) layout. If the award
    // moved to a non-tile layout and previously had categories, reset them so a stale
    // category is not persisted on a non-tile award.
    const isNowNonTile = values.offer?.presentationType !== Offer_Presentation_Type.Default;
    const hadCategories = (award.displayCategories?.length ?? 0) > 0;
    const shouldResetDisplayCategories = isNowNonTile && hadCategories;

    try {
      if (!marketingContent) {
        await marketingContentCreate(values);
      } else {
        await marketingContentUpdate({ values, dirtyFields });
      }

      if (isOfferContentDirty) {
        const displayForOrderAhead = isOrderAheadEnabled
          ? values.offer?.displayForOrderAhead ?? false
          : undefined;

        await offerUpdate({
          variables: {
            input: {
              id: award.id,
              fundingSource: values.offer?.fundingSource,
              fundingSourceName: values.offer?.fundingSourceName || null,
              presentationType: values.offer?.presentationType || Offer_Presentation_Type.Default,
              displayForOrderAhead,
            },
          },
        });
      }

      if (isDisplayCategoriesDirty || shouldResetDisplayCategories) {
        const displayCategoriesIds = shouldResetDisplayCategories
          ? []
          : (values.displayCategories ?? []).map((displayCategory) => displayCategory.id);

        await displayCategoriesCreateOrUpdate({
          offerId: award.id,
          displayCategoriesIds,
        });
      }

      refetchAward();
      if (setPageState && currentFlowType === Flow_Types.Configure) {
        setPageState(CONFIGURE_PAGE_STATE.FINALIZE);
      }
    } catch (error) {
      snackbar.error(getErrorMessage(error));
    }
  };

  const handleNextButtonClick = useCallback(() => {
    if (setPageState) {
      setPageState(CONFIGURE_PAGE_STATE.FINALIZE);
    }
  }, [setPageState]);

  const initialValues: DesignFormValues = {
    offer: {
      id: award.id,
      fundingSource: award.fundingSource,
      fundingSourceName: award.fundingSourceName,
      presentationType: award.presentationType,
      displayForOrderAhead: award.displayForOrderAhead ?? false,
    },
    marketingContent: marketingContent
      ? {
          title: marketingContent.title ?? undefined,
          shortDescription: marketingContent.shortDescription ?? undefined,
          longDescription: marketingContent.longDescription ?? undefined,
          imageUrl: marketingContent.imageUrl ?? undefined,
          terms: marketingContent.terms ?? undefined,
          termsUrl: marketingContent.termsUrl ?? undefined,
          disclaimer: marketingContent.disclaimer ?? undefined,
          disclaimerUrl: marketingContent.disclaimerUrl ?? undefined,
          externalUrl: marketingContent.externalUrl ?? undefined,
          surgeonGeneralsWarning: marketingContent.surgeonGeneralsWarning ?? undefined,
        }
      : {},
    displayCategories: award.displayCategories ?? [],
    customData: transformCustomData(marketingContent?.customData),
  };

  const designRenderForm: DesignFormProps['render'] = useCallback(
    ({
      handleSubmit,
      values,
      form,
      modifiedSinceLastSubmit,
      dirtySinceLastSubmit,
      dirty,
      submitting,
    }: DesignFormRenderProps) => {
      if (!values.marketingContent) {
        // Ensure marketingContent is always present in form values.
        // On the initial render, the object exists as expected. However, if a user fills in
        // any marketingContent field and then clears all values, Final Form automatically
        // removes the entire marketingContent object.
        //
        // Example of such issue: https://stuzo.atlassian.net/browse/OCST-14542
        values.marketingContent = {};
      }

      const isDirty = modifiedSinceLastSubmit ? dirtySinceLastSubmit : dirty;

      return (
        <OfferForm dirty={!submitting && isDirty} handleSubmit={handleSubmit}>
          <DesignFormContent
            values={values}
            isReadOnlyRole={isReadOnlyRole}
            orderAheadEnabled={isOrderAheadEnabled}
            handleCancel={() => handleCancel(isDirty)}
            handleNextButtonClick={handleNextButtonClick}
            currentFlowType={currentFlowType}
            submitting={submitting}
            dirty={isDirty}
            isLoading={loading}
          />
          <AwardPresentationTypeWatcher form={form} />
        </OfferForm>
      );
    },
    [
      isReadOnlyRole,
      isOrderAheadEnabled,
      handleNextButtonClick,
      currentFlowType,
      handleCancel,
      loading,
    ],
  );

  return (
    <Form<DesignFormValues>
      validate={validateDesignForm}
      onSubmit={onSubmit}
      render={designRenderForm}
      initialValues={initialValues}
      keepDirtyOnReinitialize
    />
  );
};
